import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Avatar } from '@producer/ui-kit';

import styles from './mention-list.module.scss';

export interface MentionItem {
  id: string;
  label: string;
  avatar?: string;
  initials?: string;
}

interface MentionListProps {
  items: MentionItem[];
  command: (item: MentionItem) => void;
}

export interface MentionListRef {
  onKeyDown: (event: { event: KeyboardEvent }) => boolean;
}

export const MentionList = forwardRef<MentionListRef, MentionListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = (index: number) => {
    const item = props.items[index];

    if (item) {
      props.command(item);
    }
  };

  const upHandler = () => {
    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % props.items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useEffect(() => setSelectedIndex(0), [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }

      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }

      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  return (
    <div className={styles['container']}>
      {props.items.length ? (
        props.items.map((item, index) => (
          <button
            className={styles['item']}
            key={item.id}
            data-selected={index === selectedIndex}
            onClick={() => selectItem(index)}
          >
            <Avatar size="xsmall" initials={item.initials} src={item.avatar} />
            <span>{item.label}</span>
          </button>
        ))
      ) : (
        <div className={styles['empty-item']}>No members found</div>
      )}
    </div>
  );
});

MentionList.displayName = 'MentionList';
